/**
 * Checkout Hook
 *
 * React Query hooks for server-side checkout processing.
 * Handles order preview calculation and complete checkout flow.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ICheckoutRequest, ICheckoutResponse, IOrderPreview } from '@order-up/models';
import { checkoutService } from '../services/checkout';
import { createLogger } from '../utils/logger';

const logger = createLogger('useCheckout');

/**
 * Preview order totals before checkout
 * Calculates exact amounts from server (single source of truth)
 *
 * `redeemPoints` (Customer Loyalty v1 — B7/B8) is the loyalty REDEMPTION
 * INTENT: a non-negative integer count of points the customer wants to apply.
 * It is part of the query key so a redemption change re-fetches a fresh
 * preview whose `total` already reflects the server-derived discount (and the
 * server echoes `loyaltyDiscount`). The client NEVER sends a dollar amount —
 * only the integer intent — so the server stays authoritative over the
 * discount and the amount-lock.
 */
export const useOrderPreview = (
    cartId: number | null,
    tipAmount: number = 0,
    redeemPoints: number = 0,
    discountCode?: string,
) => {
    // Normalize so an empty / whitespace-only code is a stable `undefined` in the
    // query key (no spurious re-fetch, byte-identical request to the no-coupon
    // shape). A change here re-runs the preview so the displayed total AND the
    // locked `expectedTotal` reflect the server-derived `couponDiscount`.
    const normalizedCode = discountCode?.trim() ? discountCode.trim() : undefined;
    return useQuery({
        queryKey: ['orderPreview', cartId, tipAmount, redeemPoints, normalizedCode],
        queryFn: async (): Promise<IOrderPreview> => {
            if (!cartId) {
                throw new Error('Cart ID is required');
            }

            logger.debug('Fetching order preview', { cartId, tipAmount, redeemPoints, discountCode: normalizedCode });
            return await checkoutService.previewOrder(cartId, tipAmount, redeemPoints, normalizedCode);
        },
        enabled: !!cartId && cartId > 0,
        staleTime: 30000, // Cache for 30 seconds
        retry: 2,
        // `tipAmount`/`redeemPoints` are part of the query key, so changing the
        // tip or redemption spins up a fresh cache entry. Keeping the previous
        // preview visible while the new one is fetched avoids the summary
        // dropping its totals to undefined (a "$0.00"/skeleton flash) on every
        // tip tweak — the customer sees the last-known totals until the
        // recalculated ones arrive. The consumer gates submit on
        // `isPlaceholderData` so the amount-lock never fires against a stale
        // (previous-tip) total.
        placeholderData: keepPreviousData,
    });
};

/**
 * Process checkout on server
 * This handles everything: cart validation, order creation, payment, and cart cleanup
 */
export const useProcessCheckout = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            request,
            idempotencyKey
        }: {
            request: ICheckoutRequest;
            idempotencyKey: string;
        }): Promise<ICheckoutResponse> => {
            logger.debug('Processing checkout');
            return await checkoutService.processCheckout(request, idempotencyKey);
        },
        onSuccess: (response) => {
            logger.info('Checkout successful', {
                orderGuid: response.orderGuid,
                orderNumber: response.orderNumber
            });

            // Invalidate cart queries (cart should be deleted on server).
            // Root key is ['carts'] (see cartKeys.all in useCart.ts) — the old
            // ['cart'] prefix-matched nothing, so the checked-out cart stayed
            // cached and reappeared on back-navigation.
            queryClient.invalidateQueries({ queryKey: ['carts'] });

            // Invalidate orders queries (new order was created)
            queryClient.invalidateQueries({ queryKey: ['orders'] });

            // Invalidate preview queries
            queryClient.invalidateQueries({ queryKey: ['orderPreview'] });
        },
        onError: (error: Error) => {
            logger.error('Checkout failed', error);
        },
    });
};

